/**
 * src/stores/catalogMigrations.ts
 *
 * Pure leaf module (Phase 37, CATALOG-01 / CATALOG-02). Mirrors the shape of
 * src/stores/projectMigrations.ts: exported top-level functions, imports no store module of any
 * kind (only the data module SERVICES_DB and the pure parseSafeNumber util), so
 * useServiceStore.ts can import from here with zero import-cycle risk.
 *
 * Owns the "two-layer catalog" mechanism:
 *   - a base layer shipped in the bundle (SERVICES_DB) that can be updated over time
 *   - a personal layer of the user's own edits, recorded via `manualFields` on each Service,
 *     that survives those base-layer updates
 */
import { Service, ServiceManualField } from '../entities/service/model/types';
import { SERVICES_DB } from '../entities/service/data/initialData';
import { parseSafeNumber } from '@/shared/lib/number';

/**
 * The five fields a user can individually claim as "theirs" on a system service.
 * Single source of truth — every field-parameterised loop in this phase iterates this tuple,
 * so adding a sixth manual field later only requires editing this one array.
 *
 * WR-03 (37-REVIEW.md): `Service.unit` (the raw unit string, e.g. 'm2') is deliberately NOT in
 * this list — only `unitType` (the claimable, UI-editable field) is. `mergeBaseCatalogIntoServices`'s
 * `...s, ...base` spread means `unit` always resolves to `base.unit` after every merge, regardless
 * of any local value. This is intentionally base-only: `ServicesTable.tsx` only exposes a
 * `unitType` `<select>`, there is no independent `unit`-editing surface today, so there is nothing
 * for a user to "claim". If a future UI surface edits `unit` directly, add `'unit'` to this tuple
 * (and to `ServiceManualField`) and wire the corresponding `manual.has('unit') ? s.unit : base.unit`
 * branch into `mergeBaseCatalogIntoServices` — do NOT assume `unitType` coverage extends to `unit`.
 */
export const MANUAL_FIELDS: readonly ServiceManualField[] = [
  'name',
  'description',
  'categoryId',
  'unitType',
  'priceLabor',
] as const;

/**
 * ONE-TIME version-0 rescue (Phase 37 backfill decision — see .planning/STATE.md's "Phase 37
 * backfill decision" entry for the full rationale and the orchestrator/plan-checker trail).
 *
 * No user before this phase has ever had a `manualFields` set — the field did not exist. The
 * merge rule this module implements (`mergeBaseCatalogIntoServices`) is "base wins on every
 * field NOT in `manualFields`", so WITHOUT this backfill the very first rehydrate after this
 * ships would revert every existing price edit and rename in a user's installed base back to
 * the bundle default — the exact opposite of what CATALOG-01 requires.
 *
 * For each non-`isCustom` service that matches a SERVICES_DB id:
 *   - claims `priceLabor` when the persisted price differs from the base price (compared via
 *     parseSafeNumber — CLAUDE.md rule 8, no re-rounding)
 *   - claims `name` when `nameKey` is ABSENT and the name differs from base (a present `nameKey`
 *     means the service is still rendered via i18n, i.e. NOT user-renamed — claiming it would be
 *     a false positive on a bundle wording change)
 *   - claims `description` when `descriptionKey` is ABSENT and the description differs from base
 *
 * Deliberately does NOT backfill `categoryId` or `unitType`: divergence there is ambiguous
 * between "the user recategorised / re-unit-typed this service" and "the bundle definition
 * changed since this blob was last written" — and it is exactly the axis the CATALOG-03
 * acceptance case (m2_floor -> m2_wall) needs base to win on. Claiming them here would
 * permanently freeze base-catalog delivery for those two fields on every existing system
 * service. This is a bounded, one-time, accepted loss (a user who customised category/unit-type
 * on a system service before this ships loses that one setting, once, at the 0->1 transition,
 * re-selectable in seconds, no money involved) — rated `costly`, not `one-way`.
 */
export function backfillManualFieldsFromDivergence(persisted: Service[]): Service[] {
  const baseById = new Map(SERVICES_DB.map((b) => [b.id, b]));

  return persisted.map((s) => {
    if (s.isCustom) return s;
    const base = baseById.get(s.id);
    if (!base) return s;

    const claims = new Set<ServiceManualField>(s.manualFields ?? []);
    let changed = false;

    if (parseSafeNumber(s.priceLabor) !== parseSafeNumber(base.priceLabor) && !claims.has('priceLabor')) {
      claims.add('priceLabor');
      changed = true;
    }
    if (!s.nameKey && s.name !== base.name && !claims.has('name')) {
      claims.add('name');
      changed = true;
    }
    if (!s.descriptionKey && s.description !== base.description && !claims.has('description')) {
      claims.add('description');
      changed = true;
    }

    if (!changed) return s;
    return { ...s, manualFields: MANUAL_FIELDS.filter((f) => claims.has(f)) };
  });
}

/**
 * The SINGLE decision point for the `nameKey`/`descriptionKey` <-> `manualFields` invariant
 * (37-REVIEW.iter2.md CR-01(new)/WR-01(new)): for each of the pairs (`name`, `nameKey`) and
 * (`description`, `descriptionKey`), the localisation key is present **iff** the field is NOT
 * claimed in `manualFields`. Claiming a field (by ANY edit, including an empty string — presence
 * in `manualFields`, never truthiness) suppresses its key; releasing the claim (reverting to the
 * bundle default) restores the key from `base`.
 *
 * Every writer of this pair — `mergeBaseCatalogIntoServices` below, `updateService` and
 * `applyServerOverrides` in `useServiceStore.ts` — MUST derive `nameKey`/`descriptionKey` by
 * calling this function rather than re-deciding the invariant independently. Three separate bugs
 * (base-merge resurrection, a missing descriptionKey clear, a truthiness-vs-presence mismatch, and
 * a missing restore-on-revert path) all trace back to the same pair being decided in more than one
 * place without a single source of truth — this function is that source of truth.
 */
export function resolveLocalizationKeys(
  base: Pick<Service, 'nameKey' | 'descriptionKey'>,
  manualFields: ReadonlySet<ServiceManualField> | readonly ServiceManualField[],
): Pick<Service, 'nameKey' | 'descriptionKey'> {
  const manual = manualFields instanceof Set ? manualFields : new Set(manualFields);
  return {
    nameKey: manual.has('name') ? undefined : base.nameKey,
    descriptionKey: manual.has('description') ? undefined : base.descriptionKey,
  };
}

/**
 * The core two-layer merge (CATALOG-01). Base wins on every field NOT present in a service's
 * own `manualFields` set; the user's own value wins on every field that IS present. `isCustom`
 * services and services with no base match are returned by IDENTITY (not even shallow-copied) —
 * asserted by `toBe` in catalogMigrations.test.ts and relied on by the mutation-proof tests to
 * detect no-op reconciliation cheaply.
 *
 * Ids present only in SERVICES_DB (added since this blob was last written) are appended, in
 * SERVICES_DB order, so the merged list's ordering is deterministic across reloads — the
 * catalog table does not reshuffle between sessions. Every price read goes through
 * `parseSafeNumber`, never re-rounded (CLAUDE.md rule 8).
 *
 * Idempotent: running this twice on the same input produces a deep-equal (not necessarily
 * reference-equal) result the second time.
 */
export function mergeBaseCatalogIntoServices(persisted: Service[]): Service[] {
  const baseById = new Map(SERVICES_DB.map((b) => [b.id, b]));
  const persistedById = new Map(persisted.map((s) => [s.id, s]));

  const reconciled = persisted.map((s) => {
    if (s.isCustom) return s;
    const base = baseById.get(s.id);
    if (!base) return s;

    const manual = new Set<ServiceManualField>(s.manualFields ?? []);
    return {
      ...s,
      ...base,
      name: manual.has('name') ? s.name : base.name,
      description: manual.has('description') ? s.description : base.description,
      categoryId: manual.has('categoryId') ? s.categoryId : base.categoryId,
      unitType: manual.has('unitType') ? s.unitType : base.unitType,
      priceLabor: manual.has('priceLabor')
        ? parseSafeNumber(s.priceLabor)
        : parseSafeNumber(base.priceLabor),
      ...resolveLocalizationKeys(base, manual),
      manualFields: s.manualFields,
    };
  });

  const newFromBundle = SERVICES_DB.filter((b) => !persistedById.has(b.id));
  return [...reconciled, ...newFromBundle];
}

/**
 * Persist `migrate` callback for `useServiceStore` (Phase 37, CATALOG-01). Version-gated:
 * `if (version < 1)` runs the one-time backfill THEN the base-catalog merge. A future
 * SERVICES_DB change that bumps `CATALOG_VERSION` to 2+ must ADD a new `if (version < N)`
 * block that ALSO calls `mergeBaseCatalogIntoServices` — bumping the constant alone does NOT
 * re-trigger the merge for users already migrated to version 1 (see the doc comment on
 * `CATALOG_VERSION` in src/entities/service/data/initialData.ts).
 *
 * The entire body is wrapped in try/catch returning `persistedState` UNCHANGED on error, for
 * the exact reason documented at projectMigrations.ts:83-98 — a throwing migrate leaves the
 * store at `create()`'s bare defaults, and the store's very next write would then permanently
 * overwrite the user's real localStorage blob with empty data. Staying stale (recoverable)
 * beats losing the data outright. This is a CLAUDE.md rule 12/13 data-safety requirement, not
 * optional hardening.
 *
 * Guards that `rawServices` is an array before touching it — a non-array (missing/null/wrong
 * type, but not itself throwing) logs a warning and returns the state untouched rather than
 * silently proceeding on bad data.
 */
export function migrateServiceStore(persistedState: unknown, version: number): unknown {
  try {
    let state = persistedState as { rawServices?: unknown };

    if (version < 1) {
      const rawServices = state?.rawServices;
      if (Array.isArray(rawServices)) {
        const backfilled = backfillManualFieldsFromDivergence(rawServices as Service[]);
        const merged = mergeBaseCatalogIntoServices(backfilled);
        state = { ...state, rawServices: merged };
      } else {
        console.warn(
          '[migrateServiceStore] rawServices is not an array on a pre-v1 blob, skipping merge:',
          rawServices,
        );
      }
    }

    return state;
  } catch (e) {
    console.warn('[migrateServiceStore] Migration failed, returning state unchanged:', e);
    return persistedState;
  }
}
